/**
 * The Steps tab's graph, as plain functions of the team document: steps become nodes and
 * hand-offs edges, laid out top to bottom on every change. Positions are never saved.
 */
import { MarkerType, type Edge as FlowEdge, type Node } from 'reactflow';
import { autoLayout } from '@/services/diagram.service';
import { emptyMember, fromList, triggerLabel, type Edge, type Member, type TeamDoc } from './catalog';

export const NODE_WIDTH = 240;
export const NODE_HEIGHT = 76;

export type StepNodeData = { name: string; model: string; starts: string };

// Nodes are keyed by position, not name, so a step being renamed stays selected.
export const stepNodeId = (i: number) => `step-${i}`;
export const stepIndex = (nodeId: string) => Number(nodeId.slice('step-'.length));
export const handoffIndex = (edgeId: string) => Number(edgeId.slice('handoff-'.length));

export const startsLabel = (m: Member) => (m.triggers.length ? m.triggers.map((t) => triggerLabel(t)).join(', ') : 'after a hand-off');

export const conditionLabel = (e: Edge) => {
  const clauses = Object.entries(e.when).map(([k, v]) => `${k} = ${fromList(v)}`);
  return clauses.length ? clauses.join(' and ') : 'any output';
};

export function stepsGraph(doc: TeamDoc): { nodes: Node<StepNodeData>[]; edges: FlowEdge[] } {
  const ids = new Map(doc.members.map((m, i) => [m.name, stepNodeId(i)] as const));
  const nodes: Node<StepNodeData>[] = doc.members.map((m, i) => ({
    id: stepNodeId(i),
    type: 'step',
    position: { x: 0, y: 0 },
    data: { name: m.name, model: m.model ?? 'sonnet', starts: startsLabel(m) },
  }));
  const edges: FlowEdge[] = doc.edges.flatMap((e, i) => {
    const source = ids.get(e.from);
    const target = ids.get(e.to);
    // A hand-off naming a step that isn't there yet (mid-edit) has nothing to draw between.
    if (!source || !target) return [];
    return [
      {
        id: `handoff-${i}`,
        source,
        target,
        type: 'smoothstep',
        label: e.gate ? `${conditionLabel(e)} · ${e.gate.connection != null ? 'Slack approval' : 'approval'}` : conditionLabel(e),
        markerEnd: { type: MarkerType.ArrowClosed },
        data: { gated: !!e.gate },
      },
    ];
  });
  const positions = autoLayout(nodes, edges, 'LINEAR', NODE_WIDTH, NODE_HEIGHT, 80, 60, 'TB');
  // A hand-off back up the graph (or to itself) leaves and enters on the right, around the
  // steps, rather than over the hand-offs coming down between the same steps.
  const up = (e: FlowEdge) => (positions[e.target]?.y ?? 0) <= (positions[e.source]?.y ?? 0);
  return {
    nodes: nodes.map((n) => ({ ...n, position: positions[n.id] ?? n.position })),
    edges: edges.map((e) => (up(e) ? { ...e, sourceHandle: 'back-out', targetHandle: 'back-in' } : { ...e, sourceHandle: 'out', targetHandle: 'in' })),
  };
}

/** A new step, last; after a step, with a hand-off to it on any output and no approval. */
export function addStep(doc: TeamDoc, after?: string): TeamDoc {
  // Hand-offs name steps, so the new one must not reuse a name left by a removed or renamed step.
  let n = doc.members.length + 1;
  while (doc.members.some((m) => m.name === `step-${n}`)) n++;
  const m = emptyMember(n);
  return {
    ...doc,
    members: [...doc.members, m],
    edges: after === undefined ? doc.edges : [...doc.edges, { from: after, when: {}, gate: null, to: m.name }],
  };
}

/** Step i replaced; a renamed step keeps its hand-offs. */
export function setStep(doc: TeamDoc, i: number, m: Member): TeamDoc {
  const old = doc.members[i]!;
  const edges =
    old.name === m.name
      ? doc.edges
      : doc.edges.map((e) => ({ ...e, from: e.from === old.name ? m.name : e.from, to: e.to === old.name ? m.name : e.to }));
  return { ...doc, members: doc.members.map((x, j) => (j === i ? m : x)), edges };
}

/** Step i and its hand-offs gone; the last step can't be removed (null). */
export function removeStep(doc: TeamDoc, i: number): TeamDoc | null {
  if (doc.members.length <= 1) return null;
  const name = doc.members[i]!.name;
  return { ...doc, members: doc.members.filter((_, j) => j !== i), edges: doc.edges.filter((e) => e.from !== name && e.to !== name) };
}
